import { Tooltip } from '@material-ui/core';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import copy from 'copy-to-clipboard';
import React, { JSX } from 'react';
import {
  REVIEW_STATUS_ALLOWED,
  REVIEW_STATUS_BLOCKED,
  REVIEW_STATUS_BLOCKED_BY_API,
  REVIEW_STATUS_REVIEWED,
  REVIEW_STATUS_UNREVIEWED,
} from '~/src/dash/api/creative-validation';
import {
  fetchSmallBidderGroupsOptionsList,
  fetchSmallBiddersOptionsList,
  fetchSmallChannelsOptionsList,
  getCreativeValidationUsersByPublisherOptionsList,
  getCreativeValidationAdomainsByPublisherOptionsList,
} from '~/src/dash/apiV2/uiOptions';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { ICreativeValidationRowData } from '~/src/dash/components/list-table';
import {
  ICustomRenderCellJSXProps,
  IListViewTableColumns,
  IListViewTableRow,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { useListViewTableStore } from '~/src/dash/components/tables/ListViewTable/context';
import { DateTimeRender } from '~/src/dash/components/tables/ListViewTable/ListViewTableRow/templates';
import { borderRadius, ct, Div, spacing, Text } from '~/src/dash/publicaUI';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { graphColors, publicaColors } from '~/src/dash/utils/colors';
import { generatePriorityList } from '~/src/dash/views/deal/forms/DealPrioritySelect';
import { generateDemandSourcesOptionsList } from '~/src/dash/api/filters/demandSources';
import {
  generateIabCategoryOptionsList,
  generateIabSubCategoryOptionsList,
} from '~/src/dash/utils/iab-category';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { convertPriorityToBidderTier } from '~/src/dash/api/headerbidders/utils_headerbidder';
import { isEmpty } from 'lodash-es';
import { addCommasToIntegerOrFloat } from '~/src/dash/utils/basic';

export const createAction = (
  newStatus: number,
  text: string,
  iconName: string,
  color: string,
): {
  newStatus: number;
  text: string;
  iconName: string;
  color: string;
} => {
  return {
    newStatus,
    text,
    iconName,
    color,
  };
};

export const formatCreativeReviewResponseIntoLVTRows = (
  creativeReviews: ICreativeValidationRowData[],
  cfg: IBiddersListViewTable,
): IListViewTableRow[] => {
  const formattedRows = [] as IListViewTableRow[];
  creativeReviews.forEach((creativeReview) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(cfg.columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;

      // if the review status is Blocked, then we only show BidErrorBlockedResponses and if the review status is Unreviewed,
      // then we show BidErrorsBrandSafety, both data will be under the column "Blocked Responses (L24H)".
      // The two bid errors data are mutually exclusive so we don't have to worry about getting both
      // BidErrorBlockedResponses and BidErrorsBrandSafety in the same creative review.
      const newResponseKey =
        creativeReview.ReviewStatus === REVIEW_STATUS_BLOCKED &&
        responseKey === 'BidErrorsBrandSafety'
          ? 'BidErrorBlockedResponses'
          : responseKey;

      // ensure value is correct
      let value: unknown = creativeReview[newResponseKey as keyof ICreativeValidationRowData];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      formattedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    formattedRows.push(formattedRow);
  });
  return formattedRows;
};

interface IBiddersListViewTable {
  columns: IListViewTableColumns;
}

const enrichmentPrefix = 'MCR-';

const CreativeReviewListModalLink = (props: ICustomRenderCellJSXProps): JSX.Element => {
  const { customFunctions } = useListViewTableStore();
  const { row, value } = props;
  const creativeReview = {
    PublisherID: row.PublisherID.value as number,
    ReviewStatus: row.CustomLeftColumn.value as number,
    Adomain: row.Adomain.value as string,
    AdomainEnrichedBy: row.AdomainEnrichedBy.value as string,
    OriginalCreativeURL: row.OriginalCreativeURL.value as string,
    CreativeURL: row.Name.value as string,
    IABCategoryCodes: row.IABCategoryCodes.value as string,
    IABCategoryEnrichedBy: row.IABCategoryEnrichedBy.value as string,
    Notes: row.Notes.value as string,
    BrandSafetyRuleIDs: row.BrandSafetyRuleIDs.value as number[],
  } as ICreativeValidationRowData;
  let icon: React.JSX.Element | undefined;
  let iconTooltip = '';

  const AdomainEnrichedBy = row.AdomainEnrichedBy.value as string;
  if (AdomainEnrichedBy.startsWith(enrichmentPrefix)) {
    const extractedID = parseInt(AdomainEnrichedBy.substring(enrichmentPrefix.length), 10);
    if (extractedID === row.PublisherID.value) {
      icon = <IconV2 name='manual-override' large />;
      iconTooltip = ct('ManualOverride');
    } else {
      icon = <IconV2 name='ai-icon' large />;
      iconTooltip = ct('DetectedByEleaAI');
    }
  } else if (AdomainEnrichedBy === 'logo') {
    icon = <IconV2 name='ai-icon' large />;
    iconTooltip = ct('DetectedByEleaAI');
  } else if (AdomainEnrichedBy === 'ctu') {
    icon = <IconV2 name='ctu' large />;
    iconTooltip = ct('ClickthroughURL');
  }

  return (
    <Div
      onClick={() => customFunctions.handleEditCreativeReviewOpen(creativeReview)}
      style={{ cursor: 'pointer', alignItems: 'center' }}
      displayFlex
    >
      {icon !== undefined && (
        <Tooltip title={iconTooltip}>
          <div>{icon}</div>
        </Tooltip>
      )}
      <Text small>{value}</Text>
    </Div>
  );
};

const IABCategoryWithModalLink = (props: ICustomRenderCellJSXProps): JSX.Element => {
  const { customFunctions } = useListViewTableStore();
  const { row, value } = props;
  const count = value.toString().split('),').length;
  const creativeReview = {
    PublisherID: row.PublisherID.value as number,
    ReviewStatus: row.CustomLeftColumn.value as number,
    Adomain: row.Adomain.value as string,
    AdomainEnrichedBy: row.AdomainEnrichedBy.value as string,
    OriginalCreativeURL: row.OriginalCreativeURL.value as string,
    CreativeURL: row.Name.value as string,
    IABCategoryCodes: row.IABCategoryCodes.value as string,
    IABCategoryEnrichedBy: row.IABCategoryEnrichedBy.value as string,
    Notes: row.Notes.value as string,
    BrandSafetyRuleIDs: row.BrandSafetyRuleIDs.value as number[],
  } as ICreativeValidationRowData;
  const displayValue = value === '' ? 'None' : value;

  let icon: React.JSX.Element | undefined;
  let iconTooltip = '';

  const IABCategoryEnrichedBy = row.IABCategoryEnrichedBy.value as string;
  if (IABCategoryEnrichedBy.startsWith(enrichmentPrefix)) {
    const extractedID = parseInt(IABCategoryEnrichedBy.substring(enrichmentPrefix.length), 10);
    if (extractedID === row.PublisherID.value) {
      icon = <IconV2 name='manual-override' large />;
      iconTooltip = ct('ManualOverride');
    } else {
      icon = <IconV2 name='ai-icon' large />;
      iconTooltip = ct('DetectedByEleaAI');
    }
  } else if (
    IABCategoryEnrichedBy === 'recognition_server' ||
    IABCategoryEnrichedBy === 'shrinker_table'
  ) {
    icon = <IconV2 name='ai-icon' large />;
    iconTooltip = ct('DetectedByEleaAI');
  }

  return (
    <Div
      onClick={() => customFunctions.handleEditCreativeReviewOpen(creativeReview)}
      style={{ cursor: 'pointer', display: 'flex' }}
    >
      {icon !== undefined && (
        <Tooltip title={iconTooltip}>
          <div>{icon}</div>
        </Tooltip>
      )}
      <Tooltip title={value.toString()}>
        <div>
          <Text
            small
            style={{
              maxWidth: '133px',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {count > 1 ? count + ' IAB Cat' : displayValue}
          </Text>
        </div>
      </Tooltip>
    </Div>
  );
};

const BidderNameWithIDRender = (props: ICustomRenderCellJSXProps): JSX.Element => {
  const { row } = props;
  const BidderName = row?.BidderName.value as string;
  const BidderID = row?.BidderID.value as number;
  return (
    <Div style={{ width: '200px' }}>
      <Tooltip title={BidderName}>
        <div>
          <Text
            small
            style={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            {BidderName}
          </Text>
          <Text extraSmall muted>
            {`ID: ${BidderID}`}
          </Text>
        </div>
      </Tooltip>
    </Div>
  );
};

const ImpressionsRender = (props: ICustomRenderCellJSXProps): JSX.Element => {
  const { value } = props;
  return <Text small>{addCommasToIntegerOrFloat(String(value))}</Text>;
};

const CreativeURLRender = (props: ICustomRenderCellJSXProps): JSX.Element => {
  const { row } = props;
  return (
    <Div onClick={() => copy(row?.Name.value as string)} style={{ cursor: 'pointer' }}>
      <Tooltip title={'Copy'}>
        <div>
          <Text small>{row?.Name.value as string}</Text>
        </div>
      </Tooltip>
    </Div>
  );
};

const ReviewAdRender = (props: ICustomRenderCellJSXProps): JSX.Element => {
  const { row } = props;
  let url = row?.Name.value as string;
  if (!url.startsWith('https://') && !url.startsWith('http://')) {
    url = row?.OriginalCreativeURL.value as string;
  }
  return (
    <a
      href={url}
      style={{
        color: 'black',
        marginRight: 22,
      }}
      target='_blank'
    >
      <PlayCircleOutlineIcon />
    </a>
  );
};

const ReviewStatusMap = {
  [REVIEW_STATUS_UNREVIEWED]: {
    text: 'Unreviewed',
    reviewStatus: 'unreviewed',
    textColor: publicaColors.black,
    backgroundColor: publicaColors.lighterGray,
    iconName: 'unreviewed',
  },
  [REVIEW_STATUS_ALLOWED]: {
    text: 'Allowed',
    reviewStatus: 'allowed',
    textColor: graphColors.green,
    backgroundColor: graphColors.green + '1a',
    iconName: 'unlock',
  },
  [REVIEW_STATUS_BLOCKED]: {
    text: 'Blocked',
    reviewStatus: 'blocked',
    textColor: publicaColors.red,
    backgroundColor: publicaColors.red + '1a',
    iconName: 'unavailable',
  },
  [REVIEW_STATUS_REVIEWED]: {
    text: 'Reviewed',
    reviewStatus: 'reviewed',
    textColor: graphColors.lavenderPurple,
    backgroundColor: graphColors.lavenderPurple + '1a',
    iconName: 'available',
  },
  [REVIEW_STATUS_BLOCKED_BY_API]: {
    text: 'API Block',
    reviewStatus: 'blocked_by_api',
    textColor: graphColors.lightOrange,
    backgroundColor: graphColors.lightOrange + '1a',
    iconName: 'pill-data',
  },
};

const ReviewStatusRender = (props: ICustomRenderCellJSXProps): JSX.Element => {
  const { row } = props;
  const statusInfo = ReviewStatusMap[row?.CustomLeftColumn.value as number];
  return (
    <Text
      small
      bold
      color={statusInfo.textColor}
      style={{
        backgroundColor: statusInfo.backgroundColor,
        borderRadius: borderRadius.small,
        width: 'fit-content',
        padding: `${spacing.none} ${spacing.small}`,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <IconV2
        name={statusInfo.iconName}
        color={statusInfo.textColor}
        style={{ marginRight: spacing.extraSmall }}
      />
      {statusInfo.text}
    </Text>
  );
};

const transformCategoryFilter = (
  _key: string,
  filters: Record<string, Set<string | number>>,
): string => {
  // Get the parent value
  const parentValue = !isEmpty(filters.IABCategory) ? Array.from(filters.IABCategory)[0] : '';

  let transformedValue = '';
  if (parentValue === 'IAB: Empty') {
    return transformedValue;
  } else if (!isEmpty(filters.IABSubCategory)) {
    transformedValue = Array.from(filters.IABSubCategory)
      .map((item) => `${parentValue}-${item}`)
      .join(',');
  } else {
    transformedValue = parentValue as string;
  }
  // Create a new Set with the transformed values
  return transformedValue;
};

const convertSetToString = (key: string, filters: Record<string, Set<string | number>>): string => {
  return Array.from(filters[key]).join(',');
};

const convertSetToPriorityString = (
  key: string,
  filters: Record<string, Set<string | number>>,
): string => {
  // Convert selectedOptionIDs Set to an array and map each element to its converted priority value
  const convertedValues = Array.from(filters[key]).map((optionID: string) =>
    convertPriorityToBidderTier(optionID.toString()),
  );
  return convertedValues.join(',');
};

export interface ICustomCreativeReviewAPIFilterInfo {
  placeholder: string;
  listLabel: string;
  selectedOptionIDs: Set<number | string>;
  displayID?: boolean;
  selectedLimit?: number;
  isSelectionRequired?: boolean;
  enableLabelTooltip?: boolean;
  enableActiveFilter?: boolean;
  singularLabel: string;
  isDisabled?: boolean;
  onLoadDisplayActiveOnly?: boolean;
  iconName: string;
  key: string;
  getList: (session: ISessionState, user: IUserState, value?: string) => Promise<ISelectOption[]>;
  childFilter?: ICustomCreativeReviewAPIFilterInfo[];
  parentKey?: string;
  isChild?: boolean;
  childKeys?: string[];
  apiKey?: string;
  shouldDisable?: (value: Set<string | number>) => boolean;
  convertForApi?: (key: string, filters: Record<string, Set<string | number>>) => string;
  disableSort?: boolean;
  addIDPrefix?: boolean;
}

export interface ICustomCreativeReviewAPIFilterConfig {
  [key: string]: ICustomCreativeReviewAPIFilterInfo;
}

export interface ICreativeReviewListViewConfig {
  cypressClassName: string;
  defaultState: {
    activeColumnKeyMap?: {
      [x: number]: string[];
    }; // default
    isTableExpanded: boolean;
    order: Order;
    orderBy: string;
    page: number;
    rowsPerPage?: RowsPerPage;
  };
  reviewStatusMap: {
    [x: number]: {
      text: string;
      reviewStatus: string;
      textColor: string;
      backgroundColor: string;
      iconName: string;
    };
  };
  columnSelector: {
    resetKeys: string[]; // what keys should be made active on reset
  };
  filters: ICustomCreativeReviewAPIFilterConfig;
  isInitialized?: boolean;
  table: IBiddersListViewTable;
  tabKey: string;
}

export const creativeReviewListViewConfig = {
  cypressClassName: 'creativereviewlistview',
  tabKey: 'All',
  defaultState: {
    activeColumnKeyMap: {
      [REVIEW_STATUS_UNREVIEWED]: [
        'ReviewAd',
        'Adomain',
        'IABCategory',
        'Impressions',
        'BidErrorsBrandSafety',
        'BidderID',
      ],
      [REVIEW_STATUS_REVIEWED]: [
        'ReviewAd',
        'Adomain',
        'IABCategory',
        'Impressions',
        'BidErrorsBrandSafety',
        'UpdatedAt',
        'User',
      ],
      [REVIEW_STATUS_BLOCKED]: [
        'ReviewAd',
        'Adomain',
        'IABCategory',
        'Impressions',
        'BidErrorsBrandSafety',
        'UpdatedAt',
        'User',
      ],
      [REVIEW_STATUS_ALLOWED]: [
        'ReviewAd',
        'Adomain',
        'IABCategory',
        'Impressions',
        'BidErrorsBrandSafety',
        'UpdatedAt',
        'User',
      ],
      [REVIEW_STATUS_BLOCKED_BY_API]: [
        'ReviewAd',
        'Adomain',
        'IABCategory',
        'Impressions',
        'BidErrorsBrandSafety',
        'UpdatedAt',
        'User',
      ],
    },
    isTableExpanded: false,
    order: 'desc',
    orderBy: '',
    page: 0,
    rowsPerPage: 25,
  },
  reviewStatusMap: ReviewStatusMap,
  columnSelector: {
    resetKeys: [
      'ReviewAd',
      'Adomain',
      'IABCategory',
      'BidderID',
      'Impressions',
      'BidErrorsBrandSafety',
      'UpdatedAt',
      'User',
    ],
  },
  filters: {
    Status: {
      iconName: 'pill-status',
      placeholder: 'Select Status',
      listLabel: 'Status',
      selectedOptionIDs: new Set([REVIEW_STATUS_UNREVIEWED]),
      singularLabel: 'Status',
      key: 'Status',
      getList: (_session, _user) => {
        return Promise.resolve([
          { id: REVIEW_STATUS_REVIEWED, label: 'Reviewed' },
          { id: REVIEW_STATUS_UNREVIEWED, label: 'Unreviewed' },
          { id: REVIEW_STATUS_ALLOWED, label: 'Allowed' },
          { id: REVIEW_STATUS_BLOCKED, label: 'Blocked' },
          { id: REVIEW_STATUS_BLOCKED_BY_API, label: 'Blocked by API' },
        ]);
      },
      selectedLimit: 1,
      isDisabled: false,
      childKeys: [
        'Reviewer',
        'Adomains',
        'Bidders',
        'Channels',
        'DemandSources',
        'BidderGroups',
        'BidderTiers',
      ],
    },
    Reviewer: {
      iconName: 'persones',
      placeholder: 'Select Reviewer',
      listLabel: 'Reviewed By',
      selectedOptionIDs: new Set(),
      singularLabel: 'Reviewer',
      key: 'Reviewer',
      getList: (session, _user, value) =>
        getCreativeValidationUsersByPublisherOptionsList(
          session.publisher.ID,
          value ? Number(value) : REVIEW_STATUS_UNREVIEWED,
        ),
      selectedLimit: 1,
      isDisabled: true,
      parentKey: 'Status',
      isChild: true,
      apiKey: 'user_filter',
      convertForApi: (key: string, filters: Record<string, Set<string | number>>) => {
        return convertSetToString(key, filters);
      },
      shouldDisable: (value: Set<string | number>): boolean => {
        const status = value.values().next().value as number;
        return status === REVIEW_STATUS_UNREVIEWED;
      },
    },
    Adomains: {
      iconName: 'globe',
      placeholder: 'Select Adomains',
      listLabel: 'Adomains',
      selectedOptionIDs: new Set(),
      singularLabel: 'Adomain',
      key: 'Adomains',
      getList: (session, _user, value) =>
        getCreativeValidationAdomainsByPublisherOptionsList(
          session.publisher.ID,
          value ? Number(value) : REVIEW_STATUS_UNREVIEWED,
        ),
      isDisabled: false,
      disableSort: true,
      parentKey: 'Status',
      isChild: true,
      apiKey: 'adomain_filter',
      convertForApi: (key: string, filters: Record<string, Set<string | number>>) => {
        return convertSetToString(key, filters);
      },
    },
    Bidders: {
      iconName: 'bidders',
      placeholder: 'Select Bidders',
      listLabel: 'Bidders',
      selectedOptionIDs: new Set(),
      singularLabel: 'Bidder',
      key: 'Bidders',
      getList: (_session, user) => fetchSmallBiddersOptionsList(user.publisher.ID),
      isDisabled: false,
      parentKey: 'Status',
      isChild: true,
      apiKey: 'bidder_filter',
      convertForApi: (key: string, filters: Record<string, Set<string | number>>) => {
        return convertSetToString(key, filters);
      },
      shouldDisable: (value: Set<string | number>): boolean => {
        const status = value.values().next().value as number;
        return [REVIEW_STATUS_REVIEWED, REVIEW_STATUS_ALLOWED].includes(status);
      },
      addIDPrefix: true,
    },
    Channels: {
      iconName: 'channels',
      placeholder: 'Select Channels',
      listLabel: 'Channels',
      selectedOptionIDs: new Set(),
      singularLabel: 'Channel',
      key: 'Channels',
      getList: (_session, user) => fetchSmallChannelsOptionsList(user.publisher.ID),
      isDisabled: false,
      parentKey: 'Status',
      isChild: true,
      apiKey: 'site_filter',
      convertForApi: (key: string, filters: Record<string, Set<string | number>>) => {
        return convertSetToString(key, filters);
      },
      shouldDisable: (value: Set<string | number>): boolean => {
        const status = value.values().next().value as number;
        return [REVIEW_STATUS_REVIEWED, REVIEW_STATUS_ALLOWED].includes(status);
      },
      addIDPrefix: true,
    },
    DemandSources: {
      iconName: 'bidders',
      placeholder: 'Select Demand Source',
      listLabel: 'Demand Sources',
      selectedOptionIDs: new Set(),
      singularLabel: 'Demand Source',
      key: 'DemandSources',
      getList: (session, user) => generateDemandSourcesOptionsList(session, user),
      isDisabled: false,
      parentKey: 'Status',
      isChild: true,
      apiKey: 'demand_source_filter',
      convertForApi: (key: string, filters: Record<string, Set<string | number>>) => {
        return convertSetToString(key, filters);
      },
      shouldDisable: (value: Set<string | number>): boolean => {
        const status = value.values().next().value as number;
        return [REVIEW_STATUS_REVIEWED, REVIEW_STATUS_ALLOWED].includes(status);
      },
    },
    BidderGroups: {
      iconName: 'bidders',
      placeholder: 'Select Bidder Group',
      listLabel: 'Bidder Groups',
      selectedOptionIDs: new Set(),
      singularLabel: 'Bidder Group',
      key: 'BidderGroups',
      getList: (_session, user) => fetchSmallBidderGroupsOptionsList(user.publisher.ID),
      isDisabled: false,
      parentKey: 'Status',
      isChild: true,
      apiKey: 'bidder_group_filter',
      convertForApi: (key: string, filters: Record<string, Set<string | number>>) => {
        return convertSetToString(key, filters);
      },
      shouldDisable: (value: Set<string | number>): boolean => {
        const status = value.values().next().value as number;
        return [REVIEW_STATUS_REVIEWED, REVIEW_STATUS_ALLOWED].includes(status);
      },
      addIDPrefix: true,
    },
    BidderTiers: {
      iconName: 'bidders',
      placeholder: 'Select Bidder Tier',
      listLabel: 'Bidder Tiers',
      selectedOptionIDs: new Set(),
      singularLabel: 'Bidder Tier',
      key: 'BidderTiers',
      getList: (_session, user) => {
        return Promise.resolve(generatePriorityList(user.publisher.ID));
      },
      isDisabled: false,
      disableSort: true,
      parentKey: 'Status',
      isChild: true,
      apiKey: 'bidder_tier_filter',
      convertForApi: (key: string, filters: Record<string, Set<string | number>>) => {
        return convertSetToPriorityString(key, filters);
      },
      shouldDisable: (value: Set<string | number>): boolean => {
        const status = value.values().next().value as number;
        return [REVIEW_STATUS_REVIEWED, REVIEW_STATUS_ALLOWED].includes(status);
      },
    },
    IABCategory: {
      iconName: 'filter',
      placeholder: 'Select IAB Category',
      listLabel: 'IAB Category',
      selectedOptionIDs: new Set(),
      singularLabel: 'IABCategory',
      key: 'IABCategory',
      getList: () => Promise.resolve(generateIabCategoryOptionsList()),
      selectedLimit: 1,
      isDisabled: false,
      disableSort: true,
      childKeys: ['IABSubCategory'],
      apiKey: 'category_filter',
      convertForApi: (key: string, filters: Record<string, Set<string | number>>) => {
        return transformCategoryFilter(key, filters);
      },
    },
    IABSubCategory: {
      iconName: 'filter',
      placeholder: 'Select IAB Sub Category',
      listLabel: 'IAB Sub Category',
      selectedOptionIDs: new Set(),
      singularLabel: 'IAB Sub Category',
      key: 'IABSubCategory',
      getList: (_session, _user, value) => {
        return Promise.resolve(generateIabSubCategoryOptionsList(value ? value : ''));
      },
      parentKey: 'IABCategory',
      disableSort: true,
      isDisabled: true,
      isChild: true,
      addIDPrefix: true,
    },
  },
  table: {
    columns: {
      ID: {
        isHidden: true,
        key: 'ID',
        label: 'ID',
        requestKey: 'ID',
        responseKey: 'ID',
        valueType: 'string',
      },
      Name: {
        cellJSX: CreativeURLRender,
        cellStyle: {
          verticalAlign: 'middle',
          minWidth: '360px',
          fontWeight: 'normal',
        },
        disableSort: true,
        key: 'Name',
        label: 'Creative URL',
        headerRenderType: 'name',
        requestKey: 'CreativeURL',
        responseKey: 'CreativeURL',
        valueType: 'string',
      },
      CustomLeftColumn: {
        cellJSX: ReviewStatusRender,
        cellStyle: {
          verticalAlign: 'middle',
        },
        headerStyle: {
          minWidth: '125px',
        },
        disableSort: true,
        headerRenderType: 'string',
        isSearchable: true,
        key: 'CustomLeftColumn',
        label: 'Status',
        requestKey: 'ReviewStatus',
        responseKey: 'ReviewStatus',
        valueType: 'string',
      },
      ReviewAd: {
        cellJSX: ReviewAdRender,
        cellRenderType: 'actions',
        cellStyle: {
          verticalAlign: 'middle',
        },
        isDefaultActive: true,
        disableSort: true,
        headerRenderType: 'actions',
        key: 'ReviewAd',
        label: 'Review Ad',
        requestKey: '',
        responseKey: '',
        valueType: 'component',
      },
      Adomain: {
        cellJSX: CreativeReviewListModalLink,
        cellRenderType: 'string',
        headerRenderType: 'string',
        isDefaultActive: true,
        key: 'Adomain',
        label: 'Adomain',
        orderByKey: 'adomain',
        requestKey: 'adomain',
        responseKey: 'Adomain',
        valueType: 'string',
      },
      IABCategory: {
        cellJSX: IABCategoryWithModalLink,
        cellRenderType: 'string',
        headerRenderType: 'string',
        isDefaultActive: true,
        disableSort: true,
        key: 'IABCategory',
        label: 'IAB Category',
        orderByKey: 'iab_category',
        requestKey: '',
        responseKey: 'IABCategory',
        valueType: 'string',
      },
      BidderID: {
        cellJSX: BidderNameWithIDRender,
        cellRenderType: 'string',
        headerRenderType: 'string',
        headerStyle: {
          minWidth: '125px',
        },
        key: 'BidderID',
        label: 'Bidder',
        orderByKey: 'BidderID',
        requestKey: '',
        responseKey: 'BidderID',
        valueType: 'number',
      },
      BidErrorsBrandSafety: {
        cellRenderType: 'number',
        headerRenderType: 'number',
        headerStyle: {
          minWidth: '170px',
        },
        isDefaultActive: true,
        key: 'BidErrorsBrandSafety',
        label: 'Blocked Responses (L24H)',
        orderByKey: 'BidErrorsBrandSafety',
        requestKey: '',
        responseKey: 'BidErrorsBrandSafety',
        valueType: 'number',
      },
      Impressions: {
        cellJSX: ImpressionsRender,
        cellRenderType: 'number',
        headerRenderType: 'number',
        isDefaultActive: true,
        key: 'Impressions',
        label: 'Impressions (L24H)',
        orderByKey: 'Impressions',
        requestKey: '',
        responseKey: 'Impressions',
        valueType: 'number',
      },
      UpdatedAt: {
        cellJSX: DateTimeRender,
        cellRenderType: 'date',
        headerRenderType: 'date',
        isDate: true,
        key: 'UpdatedAt',
        label: 'Last Updated',
        orderByKey: 'date_time',
        requestKey: '',
        responseKey: 'UpdatedAt',
        valueType: 'date',
      },
      User: {
        cellRenderType: 'string',
        headerRenderType: 'string',
        key: 'User',
        label: 'Updated By',
        orderByKey: 'user',
        requestKey: 'User',
        responseKey: 'User',
        valueType: 'string',
      },
      // hidden rows used for modal only
      IABCategoryCodes: {
        isHidden: true,
        key: 'IABCategoryCodes',
        label: 'IAB Category Codes',
        orderByKey: 'iab_category_codes',
        requestKey: '',
        responseKey: 'IABCategoryCodes',
        valueType: 'string',
      },
      PublisherID: {
        isHidden: true,
        key: 'PublisherID',
        label: 'Publisher ID',
        orderByKey: 'publisher_id',
        requestKey: '',
        responseKey: 'PublisherID',
        valueType: 'string',
      },
      Notes: {
        isHidden: true,
        key: 'Notes',
        label: 'Notes',
        orderByKey: 'Notes',
        requestKey: '',
        responseKey: 'Notes',
        valueType: 'string',
      },
      OriginalCreativeURL: {
        isHidden: true,
        key: 'OriginalCreativeURL',
        label: 'Original Creative URL',
        orderByKey: 'OriginalCreativeURL',
        requestKey: '',
        responseKey: 'OriginalCreativeURL',
        valueType: 'string',
      },
      AdomainEnrichedBy: {
        isHidden: true,
        key: 'AdomainEnrichedBy',
        label: 'Adomain Enriched By',
        orderByKey: 'AdomainEnrichedBy',
        requestKey: '',
        responseKey: 'AdomainEnrichedBy',
        valueType: 'string',
      },
      AdomainOverride: {
        isHidden: true,
        key: 'AdomainOverride',
        label: 'Adomain Override',
        orderByKey: 'AdomainOverride',
        requestKey: '',
        responseKey: 'AdomainOverride',
        valueType: 'string',
      },
      IABCategoryEnrichedBy: {
        isHidden: true,
        key: 'IABCategoryEnrichedBy',
        label: 'IAB Category Enriched By',
        orderByKey: 'IABCategoryEnrichedBy',
        requestKey: '',
        responseKey: 'IABCategoryEnrichedBy',
        valueType: 'string',
      },
      IABCategoryOverride: {
        isHidden: true,
        key: 'IABCategoryOverride',
        label: 'IAB Category Override',
        orderByKey: 'IABCategoryOverride',
        requestKey: '',
        responseKey: 'IABCategoryOverride',
        valueType: 'string',
      },
      BrandSafetyRuleIDs: {
        isHidden: true,
        key: 'BrandSafetyRuleIDs',
        label: 'Brand Safety Rule IDs',
        orderByKey: 'BrandSafetyRuleIDs',
        requestKey: '',
        responseKey: 'BrandSafetyRuleIDs',
        valueType: 'array',
      },
      BidderName: {
        isHidden: true,
        key: 'BidderName',
        label: 'Bidder Name',
        orderByKey: 'BidderName',
        requestKey: '',
        responseKey: 'BidderName',
        valueType: 'string',
      },
    },
  },
} as ICreativeReviewListViewConfig;
